'use client';
import MessageCenter from '@c/dashboard/MessageCenter';
import { orderStateNumberToStateType } from '@c/dashboard/StatusBadge';
import {
  CheckmarkIcon,
  CopyIcon,
  PencilSimpleIcon,
  RotateIcon,
  ShoppingBagIcon,
  TrashIcon,
  WarningIcon,
} from '@c/icons';
import { Warning } from '@c/icons/warning';
import AddTrackingModal from '@c/modals/AddTrackingModal';
import BaseModal from '@c/modals/BaseModal';
import CancelOrderModal from '@c/modals/CancelOrderModal';
import ChatModal from '@c/modals/ChatModal';
import ConfirmModal from '@c/modals/ConfirmModal';
import LeaveFeedbackModal from '@c/modals/LeaveFeedbackModal';
import MarkAsReceivedModal from '@c/modals/MarkAsReceivedModal';
import RefundBuyerModal from '@c/modals/RefundBuyerModal';
import TrackOrderModal from '@c/modals/TrackOrderModal';
import CustomerAndShippingInfo from '@c/order-details/CustomerAndShippingInfo';
import EarningSummary from '@c/order-details/EarningSummary';
import PaymentSummary from '@c/order-details/PaymentSummary';
import { OrderDocument, OrderItemDocument, OrderStates } from '@models/order';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Button from '@ui/Button';
import SafeImage from '@ui/SafeImage';
import Select from '@ui/Select';
import { functions } from '@util/firebase';
import { getIntegrationErrorByProductId } from '@util/firestore/integrations/integrations.service';
import { fixRateID, getLabelById } from '@util/firestore/labels/labels.service';
import {
  getChatsForOrderDetails,
  getSMSByOrderId,
} from '@util/firestore/messages';
import {
  addNewNote,
  calculateEarnings,
  deleteNote,
  getDate,
  getDealerOrderInfo,
  getOrderById,
  retryPartsUnlimited,
  transferSeller,
  updateOrderByKey,
  updateOrderDroppedOff,
  updateOrderItemState,
} from '@util/firestore/orders';
import { chargeAccount, chargeUnpaidOrder } from '@util/firestore/payments';
import {
  getProductByIdNotClean,
  getProductsByIds,
} from '@util/firestore/products';
import { getReviewByOrderIdAndSellerId } from '@util/firestore/reviews';
import {
  fixLabels,
  getRates,
  isDomestic,
  printReturnLabel,
  purchaseLabel,
  trackPackage,
  voidLabel,
} from '@util/firestore/shipengine';
import {
  getPublicUserDoc,
  getUserById,
  getUsersByIds,
} from '@util/firestore/users';
import useRealTimeOrder from '@util/hooks/useRealTimeOrder';
import {
  businessDaysAgo,
  capitalize,
  formatCurrency,
  formatTimestamp,
  isMobile,
} from '@util/index';
import { logError } from '@util/logError';
import { getProductSlug } from '@util/urlHelpers';
import {
  CANCELLATION_STATUS,
  useCancellationRequests,
} from 'app/support/hooks/useCancellationRequests';
import { useReturns } from 'app/support/hooks/useReturns';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import dayjs from 'dayjs';
import HelpRequestButton from 'features/helpWithOrder/components/HelpRequestButton';
import { ReviewCard } from 'features/reviews/components/ReviewCard';
import ContactCheckList from 'features/support/components/ContactCheckList';
import { httpsCallable } from 'firebase/functions';
import { ReturnDocument, ReturnState } from 'models/returns';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import React from 'react';
import EmptyState from '../EmptyState';
import { StatusBadgeNew } from '../StatusBadgeNew';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { ChevronUpIcon } from 'lucide-react';
import { SMSDocument } from 'models/sms';
import { StatusLabel, useOnlineStatus } from '@util/hooks/useOnlineStatus';
import { UpgradeOrderModal } from '@c/modals/UpgradeOrderModal';
import { ReturnCard } from 'app/dashboard/(dashboard)/returns/_components/ReturnCard';
import ReturnNotes from 'app/dashboard/(dashboard)/returns/_components/ReturnNotes';

const UserInfo = ({
  seller_id,
  variant,
  orderId,
}: {
  buyer_id: string;
  seller_id: string;
  variant: 'buyer' | 'seller' | 'admin';
  orderId: string;
}) => {
  const { userDoc } = useAuth();
  const { data: seller } = useQuery({
    queryKey: ['publicUser', seller_id],
    queryFn: () => getPublicUserDoc({ uid: seller_id }),
  });
  const { data: order } = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => getOrderById(orderId),
  });

  const { status } = useOnlineStatus(seller?.uid);
  if (!order || !seller) return null;

  return (
    <div className="items-between flex flex-col justify-center gap-[1.6rem] lg:items-start ">
      {seller_id && (variant === 'admin' || variant === 'buyer') && (
        <div className="flex items-center gap-[1.6rem]">
          <SafeImage
            src={seller.photo}
            alt={seller.username}
            className="h-[4rem] w-[4rem] rounded-full"
          />
          <div className="flex flex-col gap-[0.2rem]">
            <Link
              href={`/profile/${seller.username_slug}`}
              className="font-semibold hover:underline"
            >
              {seller.username}
            </Link>
            {userDoc?.roles?.admin && (
              <StatusLabel
                status={status}
                showLastLogin={true}
                recipientDocUid={seller.uid}
              />
            )}
          </div>

          {userDoc?.roles?.support &&
            !order.notes?.some(
              (note) =>
                note.text === 'Order reminders disabled' &&
                note.uid === seller_id
            ) &&
            order.sellers?.[seller_id]?.some((item) => item.state === 2) && (
              <Button
                text="Pause Order Reminders"
                onClick={async () => {
                  if (!userDoc) return;
                  try {
                    await addNewNote(
                      order.id,
                      'Order reminders disabled',
                      seller_id
                    );
                    window.location.reload();
                  } catch (e) {
                    alert((e as Error).message);
                  }
                }}
              />
            )}
        </div>
      )}
    </div>
  );
};
const OrderDetailHeader = ({
  variant,
  order,
}: {
  variant: 'buyer' | 'seller' | 'admin';
  order: OrderDocument;
}) => {
  const { userDoc } = useAuth();
  const [openModal, setOpenModal] = React.useState<'' | 'refund'>('');

  if (!userDoc) return null;
  const seller_items = order.sellers?.[userDoc.uid];
  const seller_total =
    seller_items?.reduce(
      (acc, item) =>
        acc +
        item.product_cost * item.qty +
        (item.buyer_shipping_cost ?? 0) +
        item.product_tax,
      0
    ) ?? 0;
  const allItems = Object.values(order.sellers ?? {}).reduce(
    (acc, seller) => acc.concat(seller),
    [] as OrderItemDocument[]
  );
  return (
    <div className="flex w-full items-center justify-between gap-[2.4rem] rounded-[1.5rem] bg-[#F6F6F6] p-[2rem]">
      <div className="flex w-full items-center justify-between ">
        <span className="text-[2.4rem] font-semibold ">
          {variant === 'seller' ? 'Items Sold' : 'Items Purchased'}
        </span>
        {userDoc?.roles?.support && allItems.some((i) => i.state !== 5) && (
          <>
            <Button
              type="error"
              text="Refund Item(s)"
              height="small"
              onClick={() => setOpenModal('refund')}
            />
            <RefundBuyerModal
              open={openModal === 'refund'}
              dismiss={() => setOpenModal('')}
              items={allItems.filter((item) => item.state !== 5)}
              order={order}
            />
          </>
        )}

        <div className="flex gap-[0.8rem]">
          <span className="text-[2.4rem] font-semibold">
            {variant === 'seller'
              ? formatCurrency(
                  seller_total -
                    (order.coupon?.off &&
                    order.coupon.seller_id === userDoc?.uid
                      ? order.coupon.off
                      : 0)
                )
              : formatCurrency(order.total)}
          </span>
          {/* <PaymentStatusBadge status={order.payment_id ? 'paid' : 'unpaid'} /> */}
        </div>
      </div>
    </div>
  );
};

const OrderDetailItem = ({
  item,
  variant,
  order,
  seller,
}: {
  item: OrderItemDocument;
  variant: 'buyer' | 'seller';
  order: OrderDocument;
  seller: string;
}) => {
  const [upgradeModalOpen, setUpgradeModalOpen] = React.useState(false);
  const { userDoc, user } = useAuth();
  const { showErrorToast } = useToastContext();
  const [navigatingToReturnPage, setNavigatingToReturnPage] =
    React.useState(false);
  const router = useRouter();
  const [recipientUid, setRecipientUid] = React.useState<string>('');
  const [openModal, setOpenModal] = React.useState<
    | 'track-order'
    | 'feedback'
    | 'add-tracking'
    | 'confirm'
    | 'refund'
    | 'confirm-drop-off'
    | 'mark'
    | 'chat'
    | ''
  >('');
  const [confirmLoading, setConfirmLoading] = React.useState(false);
  const { data: product, isLoading } = useQuery({
    queryKey: ['product', item.id],
    queryFn: () => getProductByIdNotClean(item.id),
  });
  const { data: returnLabel } = useQuery({
    queryKey: ['returnLabel', item.label_id],
    queryFn: () => getLabelById(item.return_label_id!),
    enabled: !!item.return_label_id,
  });
  const { data: syncError } = useQuery({
    queryKey: ['syncError', item.id, product?.integration_info?.type],
    queryFn: () => getIntegrationErrorByProductId(item.id, item.seller_id),
    enabled: !!product && product.integration_info?.type === 'shopify',
  });

  const { showSuccessToast } = useToastContext();

  const [loading, setLoading] = React.useState(false);
  const {
    data: { data: cancellationRequests },
  } = useCancellationRequests({
    order_id: order?.id,
    seller_id: item.seller_id,
    buyer_id: variant === 'buyer' ? order.buyer_id : undefined,
  });
  const hasPendingCancellationRequest = cancellationRequests?.some(
    (request) => request.status === CANCELLATION_STATUS.PENDING
  );
  const adjustFeeMutation = useMutation({
    mutationFn: async ({
      fee,
      product_id,
    }: {
      fee: number;
      product_id: string;
    }) => {
      return await httpsCallable<
        {
          fee: number;
          order_id: string;
          product_id: string;
        },
        void
      >(
        functions,
        'v2_adjustFee'
      )({
        fee,
        order_id: order.id,
        product_id,
      });
    },
    onSuccess: () => {
      showSuccessToast('Fee adjusted successfully');
      window.location.reload();
    },
    onError: (e) => {
      showErrorToast(e.message);
    },
  });

  const { returnsByOrder } = useReturns({
    orderId: order?.id,
    uid: userDoc?.uid,
    field: variant === 'seller' ? 'seller_id' : 'buyer_id',
  });
  const { data: products } = useQuery({
    queryKey: ['products', [item.id]],
    queryFn: () => getProductsByIds([item.id]),
  });

  if (isLoading || !product) return null;

  async function getReturnLabel() {
    const yes = confirm('Are you sure you want to get a return label?');
    if (!yes) return;
    try {
      setLoading(true);
      if (item.label_id) {
        const res = await printReturnLabel(item.label_id);
        const amount = +(
          res.shipment_cost.amount + res.insurance_cost.amount
        ).toFixed(2);
        await addNewNote(
          order.id,
          `Return label purchased by admin (${amount})`,
          userDoc?.uid
        );
        const charge = window.confirm(
          `Label purchased (${amount}). Would you like to charge the seller for the label?`
        );
        if (charge) {
          if (!item.account_id) throw new Error('Seller account not found');
          try {
            await chargeAccount({
              amount: amount,
              account_id: item.account_id,
              description: `Return label for order ${order.order_num}`,
              order_id: order.id,
              order_num: order.order_num,
            });
            await addNewNote(
              order.id,
              `Return label charged to seller (${amount})`,
              userDoc?.uid
            );
          } catch (e) {
            await addNewNote(
              order.id,
              `Return label charge failed (${amount})`,
              userDoc?.uid
            );
            showErrorToast((e as Error).message);
          }
        }
      } else {
        alert(
          'Getting return label for non mx locker item, this may take a minute.'
        );
        if (!product) throw new Error('Product not found');
        const sellerDoc = await getUserById(item.seller_id);
        if (!sellerDoc) throw new Error('Seller not found');
        if (!item.carrier) throw new Error('Shipping Carrier not found');
        if (!item.tracking_number) throw new Error('Tracking number not found');
        const trackingInfo = await trackPackage({
          carrier: item.carrier,
          trackingNo: item.tracking_number,
          service_code: item.service_code,
        });
        if (!trackingInfo) throw new Error('Tracking info not found');
        const firstEventZip = trackingInfo.events[0]?.postal_code;
        const ship_to =
          sellerDoc.addresses.find((a) => a.is_shop_location) ??
          sellerDoc.addresses.find((a) => a.is_default) ??
          sellerDoc.addresses?.[0];
        if (firstEventZip !== ship_to.postal_code) {
          const cont = window.confirm(
            `This ship to postal code (${ship_to.postal_code}) does not match the first tracking event (${firstEventZip}). Please confirm with the seller the correct return address. If you have already confirmed, click OK to proceed. If you need to change the address, contact a developer.`
          );
          if (!cont) return;
        }
        if (!ship_to) throw new Error('Seller address not found');
        const weight = prompt(
          "What's the weight of the return package (ounces)?"
        );
        if (!weight) return;
        const length = prompt(
          "What's the length of the return package (inch)?"
        );
        if (!length) return;
        const width = prompt("What's the width of the return package (inch)?");
        if (!width) return;
        const height = prompt(
          "What's the height of the return package (inch)?"
        );
        if (!height) return;

        alert('Getting rates...');
        const rates = await getRates({
          product_id: item.id,
          shipment: {
            ship_from: order.address,
            package: {
              weight: {
                value: parseFloat(weight),
                unit: 'ounce',
              },
              dimensions: {
                height: parseFloat(height),
                width: parseFloat(width),
                length: parseFloat(length),
                unit: 'inch',
              },
            },
          },
          ship_to,
        });
        // choose cheapest rate
        const rate = rates.reduce((acc, curr) =>
          acc.total_amount < curr.total_amount ? acc : curr
        );
        if (!rate) throw new Error('No rate found');
        alert('Purchasing label...');
        const { data } = await purchaseLabel({
          uid: order.buyer_id,
          rate_id: rate.rate_id,
          address: sellerDoc.addresses?.[0],
          order_id: order.id,
          order_num: order.order_num,
        });
        if (!data) throw new Error('Label not found');
        await addNewNote(
          order.id,
          `Return label purchased by admin (${rate.total_amount})`,
          userDoc?.uid
        );
        Object.keys(order.sellers).forEach((seller) => {
          order.sellers[seller].forEach((item) => {
            if (item.id === product.id) item.return_label_id = data.label_id;
          });
        });
        await updateOrderByKey(order.id, 'sellers', order.sellers);
        const charge = window.confirm(
          `Label purchased (${rate.total_amount}). Would you like to charge the seller for the label?`
        );
        if (charge) {
          if (!sellerDoc.account_id)
            throw new Error('Seller account not found');
          try {
            await chargeAccount({
              amount: rate.total_amount,
              account_id: sellerDoc.account_id,
              description: `Return label for order ${order.order_num}`,
              order_id: order.id,
              order_num: order.order_num,
            });
            await addNewNote(
              order.id,
              `Return label charged to seller (${rate.total_amount})`,
              userDoc?.uid
            );
          } catch (e) {
            await addNewNote(
              order.id,
              `Return label charge failed (${rate.total_amount})`,
              userDoc?.uid
            );
            showErrorToast((e as Error).message);
          }
        }
      }
      await addNewNote(
        order.id,
        'Return label generated by admin',
        userDoc?.uid
      );
      window.location.reload();
    } catch (e) {
      if (
        (e as Error).message.includes(
          'Return Services are unavailable with the requested service.'
        ) ||
        (e as Error).message.includes(
          'The Delivery Confirmation option cannot be combined with the Return Services option'
        )
      ) {
        alert(
          'Invalid label for returns endpoint. Will try creating return label manually.'
        );
        try {
          if (!product) throw new Error('Product not found');
          const sellerDoc = await getUserById(item.seller_id);
          if (!sellerDoc) throw new Error('Seller not found');
          if (!item.carrier) throw new Error('Shipping Carrier not found');
          if (!item.tracking_number)
            throw new Error('Tracking number not found');
          const trackingInfo = await trackPackage({
            carrier: item.carrier,
            trackingNo: item.tracking_number,
            service_code: item.service_code,
          });
          if (!trackingInfo) throw new Error('Tracking info not found');
          const firstEventZip = trackingInfo.events[0]?.postal_code;
          const ship_to =
            sellerDoc.addresses.find((a) => a.is_shop_location) ??
            sellerDoc.addresses.find((a) => a.is_default) ??
            sellerDoc.addresses?.[0];
          if (firstEventZip !== ship_to.postal_code) {
            const cont = window.confirm(
              `This ship to postal code (${ship_to.postal_code}) does not match the first tracking event (${firstEventZip}). Please confirm with the seller the correct return address. If you have already confirmed, click OK to proceed. If you need to change the address, contact a developer.`
            );
            if (!cont) return;
          }
          if (!ship_to) throw new Error('Seller address not found');
          const weight =
            product.shipment?.package.weight.value ??
            prompt("What's the weight of the return package (ounces)?");
          if (!weight) return;
          const length =
            product.shipment?.package.dimensions.length ??
            prompt("What's the length of the return package (inch)?");
          if (!length) return;
          const width =
            product.shipment?.package.dimensions.width ??
            prompt("What's the width of the return package (inch)?");
          if (!width) return;
          const height =
            product.shipment?.package.dimensions.height ??
            prompt("What's the height of the return package (inch)?");
          if (!height) return;

          alert('Getting rates...');
          const rates = await getRates({
            product_id: item.id,
            shipment: {
              ship_from: order.address,
              package: {
                weight: {
                  value: parseFloat(weight.toString()),
                  unit: 'ounce',
                },
                dimensions: {
                  height: parseFloat(height.toString()),
                  width: parseFloat(width.toString()),
                  length: parseFloat(length.toString()),
                  unit: 'inch',
                },
              },
            },
            ship_to,
          });
          // choose cheapest rate
          const rate = rates.reduce((acc, curr) =>
            acc.total_amount < curr.total_amount ? acc : curr
          );
          if (!rate) throw new Error('No rate found');
          alert('Purchasing label...');
          const { data } = await purchaseLabel({
            uid: order.buyer_id,
            rate_id: rate.rate_id,
            address: sellerDoc.addresses?.[0],
            order_id: order.id,
            order_num: order.order_num,
          });
          if (!data) throw new Error('Label not found');
          await addNewNote(
            order.id,
            `Return label purchased by admin (${rate.total_amount})`,
            userDoc?.uid
          );
          Object.keys(order.sellers).forEach((seller) => {
            order.sellers[seller].forEach((item) => {
              if (item.id === product.id) item.return_label_id = data.label_id;
            });
          });
          await updateOrderByKey(order.id, 'sellers', order.sellers);
          const charge = window.confirm(
            `Label purchased (${rate.total_amount}). Would you like to charge the seller for the label?`
          );
          if (charge) {
            if (!sellerDoc.account_id)
              throw new Error('Seller account not found');
            try {
              await chargeAccount({
                amount: rate.total_amount,
                account_id: sellerDoc.account_id,
                description: `Return label for order ${order.order_num}`,
                order_id: order.id,
                order_num: order.order_num,
              });
              await addNewNote(
                order.id,
                `Return label charged to seller (${rate.total_amount})`,
                userDoc?.uid
              );
            } catch (e) {
              await addNewNote(
                order.id,
                `Return label charge failed (${rate.total_amount})`,
                userDoc?.uid
              );
              showErrorToast((e as Error).message);
            }
          }
          await addNewNote(
            order.id,
            'Return label generated by admin',
            userDoc?.uid
          );
          window.location.reload();
        } catch (e) {
          showErrorToast((e as Error).message);
          await addNewNote(
            order.id,
            'Return label fallback generation failed',
            userDoc?.uid
          );
        }
      } else {
        showErrorToast((e as Error).message);
        await addNewNote(
          order.id,
          'Return label generation failed',
          userDoc?.uid
        );
      }
    } finally {
      setLoading(false);
    }
  }

  const image = item.variations
    ? product.variations?.find((v) => {
        const size = (
          item.variations?.letter ?? item.variations?.number
        )?.toString();
        const vSize = v.size.toString();
        const color = item.variations?.color;
        const vColor = v.color;
        return (
          (size === vSize && (!color || color === vColor)) ||
          (!size && color === vColor)
        );
      })?.image ?? product.images[0]?.thumb
    : product.images[0]?.thumb;

  const status = orderStateNumberToStateType(
    variant === 'seller' &&
      returnsByOrder?.some((r) => r.state === ReturnState.ReturnComplete)
      ? 3
      : item.state,
    item.rate_id,
    item.dropped_off
  );
  const variation = product.variations?.find(
    (v) =>
      (!item.variations?.letter || v.size === item.variations?.letter) &&
      (!item.variations?.number || v.size === item.variations?.number) &&
      (!item.variations?.color || v.color === item.variations.color)
  );

  return (
    <>
      <div className="flex flex-col items-start gap-[1.2rem] py-4">
        <StatusBadgeNew
          status={status}
          is_partial={item.refund_amount < item.product_cost}
          variant={variant}
          date={getDate(item, status, order)}
        />
        {cancellationRequests?.[0]?.requested_by &&
          userDoc?.roles?.admin &&
          hasPendingCancellationRequest && (
            <Link
              href={`/dashboard/messages/${cancellationRequests[0].chat_id}`}
              className="text-[1.4rem] font-semibold text-brand-secondary underline"
              target="_blank"
            >
              {capitalize(cancellationRequests[0].requested_by)} requested
              cancellation due to &quot;
              {cancellationRequests[0].reason.replace(/_/g, ' ')}&quot;
            </Link>
          )}
        {cancellationRequests?.[0] &&
          !userDoc?.roles?.admin &&
          (variant === 'seller' ||
            cancellationRequests?.[0]?.requested_by === 'buyer') &&
          hasPendingCancellationRequest && (
            <span className="text-[1.4rem] font-semibold text-brand-secondary">
              {capitalize(cancellationRequests[0].requested_by)} requested
              cancellation due to &quot;
              {cancellationRequests[0].reason.replace(/_/g, ' ')}&quot;
            </span>
          )}
        {item.refund_reason &&
          item.refund_reason === 'not_shipped_in_required_time_period' &&
          (variant === 'seller' || userDoc?.roles?.admin) && (
            <span className="text-[1.4rem] font-semibold text-brand-secondary">
              This order has been refunded because it was not shipped within our
              required timeframe. Please ensure all future orders are fulfilled
              on time to avoid cancellations.
            </span>
          )}
      </div>
      <UserInfo
        buyer_id={order.buyer_id}
        seller_id={seller}
        variant={userDoc?.roles?.admin ? 'admin' : variant}
        orderId={order.id}
      />
      {syncError && variant === 'seller' && (
        <div className="flex items-center gap-[0.4rem]">
          <WarningIcon />
          <span className="text-brand-red">
            There was an issue syncing with your shopify store. Please go to
            your{' '}
            <Link
              className="text-brand-secondary underline"
              href="/dashboard/integrations/shopify/errors"
            >
              {' '}
              error dashboard
            </Link>{' '}
            to resolve the issue.
          </span>
        </div>
      )}
      <div className="flex w-full flex-col justify-between gap-[0.8rem] lg:flex-row">
        <TrackOrderModal
          orderItem={item}
          isOpen={openModal === 'track-order'}
          dismiss={() => setOpenModal('')}
          order={order}
          sellerId={seller}
        />
        <AddTrackingModal
          isOpen={openModal === 'add-tracking'}
          dismiss={() => setOpenModal('')}
          order={order}
          order_item={item}
        />

        <div className="flex items-start gap-[1.6rem] rounded-2xl p-4 transition-colors">
          <Link
            href={getProductSlug(product)}
            className="relative  h-[10rem] w-[10rem] shrink-0 "
          >
            <SafeImage
              className="aspect-square object-contain"
              alt={'Product thumb for ' + product.title}
              src={image}
              fill
              key={image}
            />
            {item.qty > 1 && (
              <div className="absolute -right-3 -top-3 flex h-[3rem] w-[3rem] items-center justify-center rounded-full border-2 border-white bg-brand-red text-[1.2rem] font-medium text-brand-white ">
                {item.qty}
              </div>
            )}
          </Link>

          <div className="flex max-w-[40rem] flex-col gap-[0.4rem]">
            <div className="flex items-center gap-[0.4rem]">
              <Link href={getProductSlug(product)}>{product.title}</Link>
              {userDoc?.roles?.admin && (
                <button
                  type="button"
                  onClick={() => {
                    window.navigator.clipboard.writeText(product.title);
                    showSuccessToast('Copied to clipboard');
                  }}
                >
                  <CopyIcon />
                </button>
              )}
            </div>
            {product.sku && !Object.keys(item.variations ?? {}).length && (
              <span>SKU/MPN: {product.sku}</span>
            )}
            <span className="font-semibold">
              {item.qty} x {formatCurrency(item.product_cost)}
            </span>
            {item.variations?.letter ? (
              <span>Size: {item.variations.letter}</span>
            ) : product.size?.letter ? (
              <span>Size: {product.size.letter}</span>
            ) : null}
            {item.variations?.number ? (
              <span>Size: {item.variations.number}</span>
            ) : (
              product.size?.number && <span>Size: {product.size.number}</span>
            )}
            {item.variations?.color ? (
              <span>Color: {item.variations.color}</span>
            ) : null}
            {Object.keys(item.custom_inputs || {}).length > 0 && (
              <div className="mt-1 flex flex-col gap-1">
                {Object.entries(item.custom_inputs || {}).map(
                  ([key, value]) => (
                    <span key={key} className="text-[1.4rem] text-[#333333]">
                      <strong>{capitalize(key)}:</strong> {value}
                    </span>
                  )
                )}
              </div>
            )}
            {/* product sku */}
            {/* {product.sku && !Object.keys(item.variations ?? {}).length && (
              <span>Sku: {product.sku}</span>
            )} */}
            {product.purchase_entry_id &&
              (userDoc?.roles?.reseller || userDoc?.roles?.admin) && (
                <span>
                  <Link
                    className="underline"
                    href={`/dashboard/purchase-entry/${product.purchase_entry_id}`}
                  >
                    View Purchase Entry
                  </Link>
                </span>
              )}
            {/* variant sku */}
            {!!Object.keys(item.variations ?? {}).length &&
              product.variations &&
              variation?.sku && (
                <>
                  <span>SKU: {variation?.sku}</span>
                  {variation?.mpn?.length && (
                    <span>MPN: {variation?.mpn?.join(', ')}</span>
                  )}
                </>
              )}
            {userDoc?.roles?.admin && (
              <>
                <span className="font-semibold">
                  Shipping Cost: {formatCurrency(item.buyer_shipping_cost)}{' '}
                  {item.rate_id ? '(MX Locker Shipping)' : '(Flat Rate)'}
                </span>
                <span className="font-semibold">
                  Commission:{' '}
                  {formatCurrency(
                    calculateEarnings({
                      item,
                      item_count: order.item_count,
                      buyerState: order.address.state_province ?? '',
                      sellerTaxStates: [],
                      created: order.created,
                    })[1]
                  )}
                  {/* (total * 0.03 + (subtotal + shipping) * 0.05); */}
                </span>
                <span className="font-semibold">
                  Processing:{' '}
                  {formatCurrency(
                    calculateEarnings({
                      item,
                      item_count: order.item_count,
                      buyerState: order.address.state_province ?? '',
                      created: order.created,
                    })[3]
                  )}
                  {/* (total * 0.03 + (subtotal + shipping) * 0.05); */}
                </span>
                <div className="flex shrink-0 items-center gap-[1rem]">
                  {item.state === 3 && item.marked_as_received && (
                    <>
                      Marked as received:{' '}
                      {formatTimestamp(item.marked_as_received)}
                    </>
                  )}
                  {item.state === 3 &&
                    !item.marked_as_received &&
                    dayjs().diff(item.delivery_time, 'hours') >= 48 && (
                      <>48 hours passed since delivery</>
                    )}
                </div>
              </>
            )}
            <div className="flex shrink-0 items-center gap-[1rem]"></div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col items-center justify-center gap-[1.2rem] sm:min-w-[20rem]">
          {item.state === 3 &&
            variant === 'buyer' &&
            products &&
            !order.reviewed_sellers.includes(item.seller_id) && (
              <>
                <Button
                  type="secondary"
                  text="Leave Feedback"
                  className="h-[4rem] py-0"
                  width="fluid"
                  onClick={() => setOpenModal('feedback')}
                />
                {products && (
                  <LeaveFeedbackModal
                    products={products}
                    order={order}
                    reviewer_id={order.buyer_id}
                    seller_id={item.seller_id}
                    isOpen={openModal === 'feedback'}
                    dismiss={() => {
                      setOpenModal('');
                      // set html overflow to auto (to fix modal scroll bug from opening 2 modals in a row
                      setTimeout(() => {
                        document.documentElement.style.overflow = 'auto';
                      }, 500);
                    }}
                  />
                )}
              </>
            )}
          <Button
            type="secondary"
            text="Track Package"
            className="h-[4rem] py-0"
            width="fluid"
            onClick={() => setOpenModal('track-order')}
          />
          {/* <Button
              type="secondary"
              text="Mark as resolved"
              height="small"
              width="fluid"
            /> */}
          {variant === 'seller' && !item.tracking_number && !item.state && (
            <>
              <Button
                type="tertiary"
                text="Add tracking number"
                height="small"
                width="fluid"
                onClick={() => setOpenModal('add-tracking')}
              />
            </>
          )}
          {variant === 'seller' && item.tracking_number && item.state < 3 && (
            <Button
              type="tertiary"
              text="Edit tracking number"
              height="small"
              width="fluid"
              onClick={() => setOpenModal('add-tracking')}
            />
          )}
          {(variant === 'seller' || userDoc?.roles?.admin) &&
            !!item.customs_form && (
              <>
                <Button
                  type="tertiary"
                  text="View Customs Invoice"
                  target="_blank"
                  height="small"
                  width="fluid"
                  href={item.customs_form}
                />
              </>
            )}

          {(variant === 'seller' || userDoc?.roles?.support) &&
            !!item.label_pdf &&
            (item.state !== 5 || userDoc?.roles?.support) && (
              <Button
                type="outline"
                text="View Label"
                className="h-[4rem] py-0"
                width="fluid"
                href={item.label_pdf}
                target={'_blank'}
              />
            )}
          {(variant === 'seller' || userDoc?.roles?.support) &&
            !!returnLabel && (
              <Button
                type="error"
                text="View Return Label"
                className="h-[4rem] py-0"
                width="fluid"
                href={returnLabel.label_pdf}
                target={'_blank'}
              />
            )}

          {item.state === 2 && variant === 'buyer' && (
            <>
              <Button
                type="outline"
                text="Mark as Received"
                width="fluid"
                height="small"
                onClick={() => setOpenModal('mark')}
              />
              <MarkAsReceivedModal
                isOpen={openModal === 'mark'}
                dismiss={() => setOpenModal('')}
                order={order}
                seller_id={item.seller_id}
                onSubmit={() => {
                  // fetch order again
                  getOrderById(order.id).then((order) => {
                    const items = order?.sellers?.[item.seller_id];
                    if (items?.every((item) => item.state === 3)) {
                      setOpenModal('feedback');
                    }
                  });
                }}
              />
            </>
          )}
          {order.sellers?.[item.seller_id]?.some(
            (item) => item.state === 2 || item.state === 3 || item.state === 7 // delivered, completed, or on hold
          ) && (
            <>
              {variant === 'buyer' && !returnsByOrder?.length && (
                <>
                  <Button
                    type="outline"
                    text="Return Item(s)"
                    height="small"
                    loading={navigatingToReturnPage}
                    onClick={() => {
                      setNavigatingToReturnPage(true);
                      router.push(
                        `/dashboard/purchases/${order.id}/return/${item.seller_id}`
                      );
                    }}
                    width="fluid"
                  />
                </>
              )}
              {!!returnsByOrder?.length &&
                (userDoc?.roles?.admin || variant === 'buyer') && (
                  <Button
                    type="outline"
                    text="View Return Status"
                    height="small"
                    href={`/dashboard/returns/${returnsByOrder[0].id}`}
                  />
                )}
            </>
          )}
          {!!item.tracking_number &&
            !item.dropped_off &&
            item.state === 0 &&
            variant === 'seller' && (
              <>
                <Button
                  type="outline"
                  text="Confirm as Sent"
                  className="h-[4rem] py-0"
                  width="fluid"
                  loading={confirmLoading}
                  onClick={() => setOpenModal('confirm-drop-off')}
                />
                <ConfirmModal
                  btnType="success"
                  buttonText="Confirm"
                  icon={<CheckmarkIcon />}
                  title={'Confirm as sent'}
                  body={
                    'Click confirm if you have dropped off the package with the carrier'
                  }
                  isOpen={openModal === 'confirm-drop-off'}
                  dismiss={async (confirm) => {
                    if (confirm) {
                      try {
                        setConfirmLoading(true);
                        await updateOrderDroppedOff(order, item);
                        await addNewNote(
                          order.id,
                          `Item ${item.id} updated to dropped off`,
                          userDoc?.uid
                        );
                        window.location.reload();
                      } catch (e) {
                        const errorId = Math.random().toString(36).substring(7);
                        logError(e, errorId);
                        alert(
                          'Error confirming as sent. Please contact support with this error ID: ' +
                            errorId
                        );
                      } finally {
                        setConfirmLoading(false);
                      }
                    } else {
                      setOpenModal('');
                    }
                  }}
                ></ConfirmModal>
              </>
            )}
          {!!userDoc?.roles?.admin && (
            <div className="flex flex-col gap-[1.6rem]">
              <Select
                options={OrderStates.map((state, index) => ({
                  label: !index && item.rate_id ? 'Awaiting Drop-off' : state,
                  value: index.toString(),
                  id: index.toString(),
                }))}
                value={item.state + ''}
                onChange={async (selectOption) => {
                  const prevState = item.state;
                  await updateOrderItemState(
                    order,
                    seller,
                    item.id,
                    parseInt(selectOption.value)
                  );
                  await addNewNote(
                    order.id,
                    `Item state changed to ${
                      OrderStates[parseInt(selectOption.value)]
                    } from ${OrderStates[prevState]}`,
                    userDoc.uid
                  );
                  window.location.reload();
                }}
                allowEmpty={false}
              />
              {!item.tracking_number && !item.state && (
                <>
                  <Button
                    type="tertiary"
                    text="Add Tracking Number"
                    height="small"
                    onClick={() => setOpenModal('add-tracking')}
                  />
                </>
              )}
              {((item.tracking_number && !item.rate_id && item.state < 3) ||
                (item.tracking_number && userDoc?.roles?.admin)) && (
                <Button
                  type="tertiary"
                  text="Edit Tracking Number"
                  height="small"
                  onClick={() => setOpenModal('add-tracking')}
                />
              )}
              {!!item.label_id && (
                <Button
                  text="Void Label"
                  height="small"
                  loading={loading}
                  onClick={async () => {
                    const yes = confirm(
                      'Voiding this label is irreversible. If desired, you must manually change the order status of this item afterwards.'
                    );
                    if (yes) {
                      try {
                        setLoading(true);
                        const res = await voidLabel(
                          item.label_id!,
                          item.tracking_number
                        );
                        if (!res.approved && res.message) {
                          alert(res.message);
                          return;
                        }
                        await addNewNote(
                          order.id,
                          `Label ${item.label_id} voided by admin`,
                          userDoc.uid
                        );
                      } catch (e) {
                        showErrorToast((e as Error).message);
                      } finally {
                        setLoading(false);
                      }
                    }
                  }}
                />
              )}
              {(item.state === 2 || item.state === 7) &&
                !item.return_label_id && (
                  <Button
                    type="error"
                    text="Get Return Label"
                    height="small"
                    loading={loading}
                    noXPadding={isMobile()}
                    onClick={getReturnLabel}
                  />
                )}
              {order.total_state !== 2 && (
                <Button
                  text="Transfer Seller"
                  height="small"
                  onClick={() =>
                    transferSeller(order, item, item.seller_id!, seller)
                  }
                />
              )}
              {userDoc?.roles?.admin && (
                <Button
                  type="secondary"
                  text="Adjust Fee"
                  height="small"
                  onClick={() => {
                    const fee = prompt(
                      'Enter the new fee as a percentage (min 0, max 10)'
                    );
                    if (!fee) return;
                    const numFee = parseInt(fee);
                    if (isNaN(numFee) || numFee < 0 || numFee > 10) {
                      alert('Invalid fee');
                    } else {
                      adjustFeeMutation.mutate({
                        fee: numFee / 100,
                        product_id: item.id,
                      });
                    }
                  }}
                />
              )}
              {/* Upgrade button for admin */}
              {userDoc?.roles?.admin && (
                <>
                  <Button
                    type="error"
                    text="Upgrade"
                    height="small"
                    onClick={() => setUpgradeModalOpen(true)}
                  />
                  <UpgradeOrderModal
                    open={upgradeModalOpen}
                    onClose={() => setUpgradeModalOpen(false)}
                    seller_id={item.seller_id}
                    order={order}
                    item_id={item.id}
                  />
                </>
              )}
            </div>
          )}
          {variant === 'buyer' && (
            <Button
              type="outline"
              text={`Contact Seller`}
              onClick={() => {
                if (user?.isAnonymous) {
                  router.push('/verify-phone');
                } else {
                  setRecipientUid(item.seller_id);
                  setOpenModal('chat');
                }
              }}
              width="fluid"
              className="h-[4rem] py-0"
            />
          )}

          <ChatModal
            isOpen={openModal === 'chat'}
            dismiss={() => setOpenModal('')}
            recipientUid={recipientUid}
            order={order!}
          />
        </div>
      </div>
    </>
  );
};

const OrderDetailCard = ({
  order,
  items,
  seller,
  variant,
}: {
  order: OrderDocument;
  items?: OrderItemDocument[];
  seller: string;
  variant: 'buyer' | 'seller';
}) => {
  if (!items) return null;

  return (
    <>
      <div className="flex w-full flex-col justify-center gap-[1.6rem] border-t-[1px] p-[2.4rem]">
        {/* Item info */}
        <div className="flex w-full items-center justify-between gap-[4rem]">
          {/* Items */}
          <div className="flex w-full flex-col gap-[1.6rem]">
            {items.map((item) => (
              <OrderDetailItem
                order={order}
                seller={seller}
                variant={variant}
                key={item.id}
                item={item}
              />
            ))}
            {order.is_offer ? (
              <span className="font-semibold italic text-brand-green">
                Offer Accepted -{' '}
                {formatCurrency(
                  Object.values(order.sellers ?? {})?.[0]?.[0]?.product_cost ??
                    0
                )}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
};

const OrderReturns = ({
  returns,
  variant,
}: {
  returns: ReturnDocument[];
  variant: 'buyer' | 'seller';
}) => {
  return (
    <div className="flex flex-col items-start gap-[1.6rem]">
      {returns.map((ret) => (
        <ReturnCard
          hideHeader
          key={ret.id}
          ret={ret}
          variant={variant}
          showReturnLink
        />
      ))}
    </div>
  );
};
interface OrderDetailsPageProps {
  order_id: string;
  variant: 'buyer' | 'seller';
}
const OrderDetailsPage = ({ order_id, variant }: OrderDetailsPageProps) => {
  const order = useRealTimeOrder(order_id);
  const { userDoc } = useAuth();
  const { data: messages } = useQuery({
    queryKey: ['orderMessages', order_id],
    enabled: !!userDoc?.roles?.admin && !!order,
    queryFn: () => getChatsForOrderDetails(order!),
  });

  const { data: products } = useQuery({
    queryKey: ['products', order?.id],
    queryFn: () =>
      getProductsByIds(
        Object.values(order?.sellers ?? {})
          .flat()
          .map((i) => i.id),
        true
      ),
  });

  const { data: smsList } = useQuery({
    queryKey: ['sms', order?.id],
    queryFn: () => getSMSByOrderId(order!.id),
    enabled: !!order && !!userDoc?.roles?.admin,
  });

  const [cancelOrderOpen, setCancelOrderOpen] = React.useState(false);
  const [adminModalOpen, setAdminModalOpen] = React.useState(false);
  const [newNote, setNewNote] = React.useState('');

  const queryClient = useQueryClient();
  const [loading, setLoading] = React.useState(false);
  const {
    data: { data: cancellationRequests },
  } = useCancellationRequests({
    order_id: order?.id,
    seller_id:
      userDoc?.uid === process.env.NEXT_PUBLIC_SUPPORT_ID
        ? undefined
        : userDoc?.uid,
    buyer_id: variant === 'buyer' ? order?.buyer_id : undefined,
  });
  const hasCancellationRequest = cancellationRequests?.some(
    (request) => request.status === CANCELLATION_STATUS.PENDING
  );
  const { showSuccessToast, showErrorToast } = useToastContext();
  const retryPartsUnlimitedMutation = useMutation({
    mutationFn: async () => {
      if (!order) return;
      return await retryPartsUnlimited(order);
    },
    onSuccess: (data) => {
      if (!data) return;
      if (data.success) {
        showSuccessToast(data.message);
        window.location.reload();
      } else {
        showErrorToast(data.message);
      }
    },
    onError: (e) => {
      showErrorToast(e.message);
    },
  });

  const [dealerOrderInfo, setDealerOrderInfo] = React.useState<{
    type: 'wps' | 'parts_unlimited' | 'helmet_house';
    label: string;
    order: object;
  } | null>(null);
  const getDealerOrderInfoMutation = useMutation({
    mutationFn: async (type: 'wps' | 'parts_unlimited' | 'helmet_house') => {
      if (!order) return;
      return await getDealerOrderInfo(type, order);
    },
    onSuccess: (data) => {
      if (!data) return;
      const label =
        data.type === 'wps'
          ? 'WPS'
          : data.type === 'parts_unlimited'
          ? 'Parts Unlimited'
          : 'Helmet House';
      setDealerOrderInfo({ ...data, label });
    },
    onError: (e) => {
      showErrorToast(e.message);
    },
  });
  const { returnsByOrder } = useReturns({
    orderId: order?.id,
    uid: userDoc?.uid,
    field: variant === 'seller' ? 'seller_id' : 'buyer_id',
  });
  if (!userDoc) return null;

  if (!order) {
    return (
      <EmptyState
        icon={<ShoppingBagIcon />}
        title={'No order with this ID'}
        description={'Try refreshing the page if you expected an order here.'}
      />
    );
  }

  if (
    variant === 'buyer' &&
    order.buyer_id !== userDoc.uid &&
    !userDoc.roles?.support
  ) {
    return (
      <EmptyState
        icon={<ShoppingBagIcon />}
        title={'No order with this ID'}
        description={'Try refreshing the page if you expected an order here.'}
      />
    );
  }

  if (
    variant === 'seller' &&
    !order.seller_arr.includes(userDoc.uid) &&
    !userDoc.roles?.support
  ) {
    return (
      <EmptyState
        icon={<ShoppingBagIcon />}
        title={'No order with this ID'}
        description={'Try refreshing the page if you expected an order here.'}
      />
    );
  }

  // attaches account_id to order items if it's missing
  const takeOffHold = async () => {
    if (!order.sellers) return;
    const sellersToGet = new Set<string>();
    Object.keys(order.sellers).forEach((seller) =>
      order.sellers![seller].forEach((item) => {
        if (!item.account_id) sellersToGet.add(seller);
      })
    );
    if (sellersToGet.size) {
      const users = await getUsersByIds([...sellersToGet]);
      if (users.some((s) => !s.account_id))
        return alert("Seller still hasn't completed their onboarding");
    }
    await updateOrderByKey(order.id, 'total_state', 0);
    await addNewNote(order.id, 'Order taken off hold by admin', userDoc.uid);
    queryClient.invalidateQueries({
      queryKey: ['order', order.id],
    });
    queryClient.invalidateQueries({
      queryKey: ['purchases'],
    });
  };

  const fixShippingLabels = async () => {
    try {
      setLoading(true);
      const { updated, errors } = await fixLabels(order.id);
      if (errors.length) alert(errors.join('\n'));
      else alert(`${updated} labels fixed!`);
      await addNewNote(
        order.id,
        `${updated} labels fixed by admin`,
        userDoc.uid
      );
      queryClient.invalidateQueries({
        queryKey: ['order', order.id],
      });
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const refreshRateId = async () => {
    try {
      setLoading(true);
      const items = Object.values(order.sellers ?? {}).flat();
      const elligibleItems = items.filter((i) => {
        if (i.rate_id && !i.label_pdf) return true;
        const product = products?.find((p) => p.id === i.id);
        if (product && !product.is_flat_rate && !i.rate_id) return true;
        return false;
      });
      if (elligibleItems.length) {
        await Promise.all(
          elligibleItems.map(async (item) => {
            const product = products?.find((p) => p.id === item.id);
            if (!product) {
              alert(`Product not found for item ${item.id}.`);
              return;
            }
            const result = await fixRateID(
              product,
              order,
              item,
              item.seller_id
            );
            if (result) {
              const { oldRateID, newRateID } = result;
              await addNewNote(
                order.id,
                `Rate ID refreshed from ${
                  oldRateID || 'none'
                } to ${newRateID} for item ${item.id}`,
                userDoc.uid
              );
              window.location.reload();
            }
          })
        );
      } else {
        throw new Error('Every item has a label, nothing to do.');
      }
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  let needsLabel: boolean = false;
  let expeditedShipping = new Map<string, boolean>();
  let internationalOrder = new Map<string, boolean>();

  if (order?.sellers) {
    if (userDoc?.roles?.admin) {
      const sellers = Object.keys(order.sellers);
      sellers.forEach((seller) => {
        order.sellers?.[seller]?.forEach((item) => {
          const product = products?.find((p) => p.id === item.id);
          if (
            (!!item.rate_id && !item.label_pdf) ||
            (product &&
              !product.is_flat_rate &&
              !item.rate_id &&
              !isDomestic(product.shipment, order.address))
          ) {
            needsLabel = true;
          }
          if (
            item.service_code &&
            (item.service_code.toLowerCase().includes('expedited') ||
              item.service_code.toLowerCase().includes('air') ||
              item.service_code.toLowerCase().includes('express'))
          )
            expeditedShipping.set(seller, true);

          const shipFromCountry =
            userDoc!.addresses?.find((a) => a.is_shop_location)?.country_code ??
            userDoc!.addresses?.[0]?.country_code ??
            userDoc!.country?.code ??
            'US';
          if (
            item.customs_form ||
            order.address.country_code !== shipFromCountry
          )
            internationalOrder.set(seller, true);
        });
      });
    } else if (variant === 'seller') {
      const seller = userDoc?.uid;
      order.sellers?.[seller]?.forEach((item) => {
        if (
          item.service_code &&
          (item.service_code.toLowerCase().includes('expedited') ||
            item.service_code.toLowerCase().includes('air') ||
            item.service_code.toLowerCase().includes('express'))
        )
          expeditedShipping.set(seller, true);

        const shipFromCountry =
          userDoc!.addresses?.find((a) => a.is_shop_location)?.country_code ??
          userDoc!.addresses?.[0]?.country_code ??
          userDoc!.country?.code ??
          'US';
        if (item.customs_form || order.address.country_code !== shipFromCountry)
          internationalOrder.set(seller, true);
      });
    }
  }

  const notes = userDoc?.roles?.admin
    ? order.notes
    : order.notes?.filter((n) => n.uid === userDoc.uid);

  return (
    <div className="flex flex-col items-start gap-[2.4rem]  lg:px-[2rem] lg:pt-0">
      {/* Order details body */}
      <div className="border-brand-gray-light flex w-full flex-col items-start py-[2.4rem]  lg:gap-[4rem]">
        <div className="flex w-full flex-col items-center justify-between gap-[1rem] px-4 sm:px-0 lg:flex-row lg:gap-[3.2rem]">
          <div className="flex w-full items-center lg:justify-between">
            <div className="flex items-center">
              <div className="flex flex-col">
                <h1
                  className="flex items-center gap-x-2 text-[2.4rem] font-semibold leading-[3.2rem] text-brand-lightest-black"
                  onClick={() => {
                    if (userDoc?.roles?.admin)
                      window.open(
                        `https://console.firebase.google.com/project/${process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID}/firestore/data/~2Forders~2F${order.id}`,
                        '_blank'
                      );
                  }}
                >
                  Order #
                  {!order?.order_num || order.order_num === 10_000 ? (
                    <div className="h-12 w-32 animate-pulse rounded-full bg-slate-500"></div>
                  ) : (
                    order.order_num
                  )}
                </h1>
                <span className="leading-[2.4rem]">
                  {formatTimestamp(order.created)}
                </span>
                {userDoc?.roles?.support && (
                  <span className="leading-[2.4rem]">
                    {businessDaysAgo(order.created)} business days ago
                  </span>
                )}
              </div>

              <div className="flex flex-col">
                {order.total_state === 3 && userDoc?.roles?.admin && (
                  // order is on hold
                  <div className="mx-[1.6rem] flex items-center gap-[0.8rem] rounded-full bg-brand-red p-4">
                    {/* exclamation mark */}
                    {/* <span className="text-[1.4rem] font-semibold text-white">
                    {'!'}
                  </span> */}
                    <WarningIcon fill="#FFFFFF" />
                    <span className="text-[1.4rem] font-semibold text-white">
                      On Hold
                    </span>
                  </div>
                )}
              </div>
            </div>

            {userDoc?.roles?.admin && (
              <>
                {order.total_state === 3 && (
                  <Button
                    type="success"
                    text="Take Off Hold"
                    height="small"
                    noXPadding={isMobile()}
                    onClick={takeOffHold}
                  />
                )}
                {needsLabel && (
                  <div className="flex gap-[1.6rem]">
                    <Button
                      type="error"
                      text="Fix Labels"
                      height="small"
                      loading={loading}
                      noXPadding={isMobile()}
                      onClick={fixShippingLabels}
                    />
                    <Button
                      text="Get New Rate ID"
                      onClick={refreshRateId}
                      height="small"
                      loading={loading}
                    />
                  </div>
                )}
                {order.notes?.some(
                  (note) =>
                    note.text.includes(
                      'Failed to create order for Parts Unlimited Item'
                    ) &&
                    !order.notes?.some((note) =>
                      note.text.includes(
                        'Parts Unlimited Order Created Successfully.'
                      )
                    )
                ) && (
                  <Button
                    text="Retry Parts Unlimited Order (s)"
                    onClick={() => retryPartsUnlimitedMutation.mutate()}
                    loading={retryPartsUnlimitedMutation.isPending}
                    disabled={retryPartsUnlimitedMutation.isPending}
                  />
                )}
                {!!products?.some(
                  (p) => p.integration_info?.type === 'wps'
                ) && (
                  <Button
                    text="Get WPS Order Info"
                    onClick={() => getDealerOrderInfoMutation.mutate('wps')}
                    loading={getDealerOrderInfoMutation.isPending}
                    disabled={getDealerOrderInfoMutation.isPending}
                  />
                )}
                {!!products?.some(
                  (p) => p.integration_info?.type === 'parts_unlimited'
                ) && (
                  <Button
                    text="Get Parts Unlimited Order Info"
                    onClick={() =>
                      getDealerOrderInfoMutation.mutate('parts_unlimited')
                    }
                    loading={getDealerOrderInfoMutation.isPending}
                    disabled={getDealerOrderInfoMutation.isPending}
                  />
                )}
                {!!products?.some(
                  (p) => p.integration_info?.type === 'helmet_house'
                ) && (
                  <Button
                    text="Get Helmet House Order Info"
                    onClick={() =>
                      getDealerOrderInfoMutation.mutate('helmet_house')
                    }
                    loading={getDealerOrderInfoMutation.isPending}
                    disabled={getDealerOrderInfoMutation.isPending}
                  />
                )}
                {!!dealerOrderInfo && (
                  <ConfirmModal
                    title={`${dealerOrderInfo.label} Order Info`}
                    body=""
                    icon={<RotateIcon />}
                    fullWidth
                    isOpen={!!dealerOrderInfo}
                    dismiss={() => {
                      setDealerOrderInfo(null);
                    }}
                    buttonText="Close"
                  >
                    <pre className="text-left">
                      {JSON.stringify(dealerOrderInfo.order, null, 2)}
                    </pre>
                  </ConfirmModal>
                )}

                {!order.payment_id && (
                  <div>
                    <Link
                      href={`https://dashboard.stripe.com/customers/${order.customer_id}`}
                      target="_blank"
                      className="font-brand-secondary font-semibold text-brand-secondary hover:underline"
                    >
                      View Payment Methods
                    </Link>
                    <Button
                      type="secondary"
                      text="Charge Buyer"
                      height="small"
                      noXPadding={isMobile()}
                      onClick={() => {
                        const p = prompt('Enter payment method id: pm_123...');
                        if (p) {
                          chargeUnpaidOrder(order.order_num, p)
                            .then(() => {
                              window.location.reload();
                            })
                            .catch((e) => {
                              alert((e as Error).message);
                            });
                        }
                      }}
                    />
                  </div>
                )}
              </>
            )}
            {!userDoc?.roles?.admin && (
              <div className="hidden sm:block">
                <HelpRequestButton order_id={order.id} variant={variant} />
              </div>
            )}
          </div>

          {variant === 'seller' && (
            <div className="mb-4 flex items-center gap-[1.6rem] sm:mb-0">
              {order.sellers?.[userDoc.uid].some((item) => !item.rate_id) && (
                <Button
                  type="secondary"
                  text="Print Label"
                  className="h-[4rem] py-0"
                  href={`/dashboard/shipping-center/create?order_num=${order.order_num}`}
                  width="small"
                />
              )}
            </div>
          )}
        </div>
        {expeditedShipping.size > 0 &&
          (expeditedShipping.get(userDoc.uid) || userDoc?.roles?.admin) && (
            <div className="w-full space-y-4 rounded-brand border-2 border-brand-secondary bg-red-300/30 p-4">
              <div className="flex items-center gap-4 font-semibold">
                <Warning /> This buyer paid for expedited shipping
              </div>
              <div>
                Please make sure to ship this order as soon as possible.
              </div>
            </div>
          )}
        {internationalOrder?.size > 0 &&
          (internationalOrder.get(userDoc.uid) || userDoc?.roles?.admin) && (
            <div className="w-full space-y-4 rounded-brand border-2 border-brand-secondary bg-red-300/30 p-4">
              <div className="flex items-center gap-4 font-semibold">
                <Warning /> This order may require customs forms!
              </div>
              <div>
                Please make sure to print and attach the customs form to the
                outside of the shipment box to avoid it being sent back. If you
                need help, please contact support.
              </div>
            </div>
          )}
        {/* Order confirmation */}
        {!!returnsByOrder?.length && (
          <div className="flex w-full flex-col items-start justify-center border-[1px] border-solid border-[#D9D9D9]  lg:rounded-[1.5rem]">
            <div className="flex w-full gap-[2.4rem] rounded-[1.5rem] bg-[#F6F6F6] p-[2rem]">
              <span className="text-[2.4rem] font-semibold ">
                Returned Item(s)
              </span>
            </div>

            <OrderReturns variant={variant} returns={returnsByOrder} />
          </div>
        )}
        <div className="flex w-full flex-col items-start justify-center border-[1px] border-solid border-[#D9D9D9]  lg:rounded-[1.5rem]">
          <OrderDetailHeader variant={variant} order={order} />
          {variant === 'buyer' ? (
            Object.entries(order.sellers ?? {})
              .sort((a, b) => b[0].localeCompare(a[0]))
              .map(([seller, items]) =>
                items.map((item) => (
                  <OrderDetailCard
                    order={order}
                    items={[item]}
                    key={item.id}
                    seller={seller}
                    variant="buyer"
                  />
                ))
              )
          ) : userDoc ? (
            <OrderDetailCard
              order={order}
              items={order.sellers?.[userDoc.uid]}
              seller={userDoc.uid}
              variant="seller"
            />
          ) : null}
        </div>
        <div className="w-full p-8 sm:hidden">
          <HelpRequestButton
            className="h-[5.5rem]"
            order_id={order.id}
            variant={variant}
          />
        </div>
        <div className="flex w-full flex-col gap-[1.6rem] xl:flex-row">
          <CustomerAndShippingInfo order={order} variant={variant} />
          {variant === 'buyer' && !userDoc.roles?.admin ? (
            <PaymentSummary order={order} />
          ) : (
            <EarningSummary order={order} />
          )}
        </div>
        {variant === 'seller' && (
          <OrderReviews order={order} sellerId={userDoc.uid} />
        )}
        {(userDoc?.roles?.admin || variant === 'buyer') && (
          <div className="flex w-full flex-col gap-[1.6rem]">
            {order.seller_arr.map((seller) => (
              <OrderReviews order={order} sellerId={seller} key={seller} />
            ))}
          </div>
        )}

        {variant === 'seller' &&
          order.sellers?.[userDoc?.uid]?.some((item) => item.state === 0) &&
          !order.offer_id &&
          !hasCancellationRequest && (
            <div className="p-4">
              <Button
                text="Cancel Order"
                height="small"
                type="secondary"
                onClick={() => setCancelOrderOpen(true)}
              />
              <CancelOrderModal
                isOpen={cancelOrderOpen}
                dismiss={() => setCancelOrderOpen(false)}
                order={order}
              />
            </div>
          )}

        {(userDoc?.roles?.admin || order.seller_arr.includes(userDoc?.uid)) && (
          <>
            {userDoc.roles?.admin && <PaymentSummary order={order} />}
            <div className="flex w-full flex-col gap-[2.4rem] rounded-2xl p-[2.4rem] lg:border-[1px]">
              <div className="text-[1.8rem flex w-full justify-between gap-[0.8rem] font-semibold">
                <span>Order Notes</span>
                <Button
                  text="Save"
                  height="extraSmall"
                  width="extraSmall"
                  type="text"
                  onClick={async () => {
                    await addNewNote(order.id, newNote, userDoc?.uid);
                    queryClient.invalidateQueries({
                      queryKey: ['order', order.id],
                    });
                    setNewNote('');
                  }}
                  trailingIcon={<PencilSimpleIcon />}
                />
              </div>
              <div className="flex flex-col gap-[1.2rem]">
                <div className="flex w-full">
                  <span className="w-[10rem] text-brand-gray">Notes</span>
                  <span className="grow">
                    <textarea
                      className="h-[10rem] w-full rounded-lg border-[1px] border-gray-300 p-[1.2rem]"
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Add notes here..."
                    ></textarea>
                  </span>
                </div>
              </div>
            </div>
            {/* display all notes here */}

            {notes && notes.length > 0 && (
              <div className="flex w-full flex-col gap-[2.4rem] rounded-2xl p-[2.4rem] lg:border-[1px]">
                <div className="text-[1.8rem flex w-full justify-between gap-[0.8rem] font-semibold">
                  <span>Previous Notes</span>
                </div>
                {notes
                  .sort((a, b) => b.created - a.created)
                  .map((note) => (
                    <div
                      className="flex flex-col gap-[1.2rem]"
                      key={note?.created}
                    >
                      <div className="flex w-full justify-between">
                        <div className="flex gap-[0.8rem]">
                          <span className="w-[10rem] text-brand-gray">
                            {new Date(note?.created!).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: 'numeric',
                              minute: 'numeric',
                              hour12: true,
                            })}
                          </span>
                          <span className="grow">{note?.text}</span>
                        </div>
                        <div className=" font-semibold text-brand-red">
                          <Button
                            text=""
                            type="text"
                            trailingIcon={<TrashIcon />}
                            onClick={() => {
                              deleteNote(order.id, note);
                              queryClient.invalidateQueries({
                                queryKey: ['order', order.id],
                              });
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </>
        )}

        {userDoc?.roles?.admin && !!returnsByOrder?.length && (
          <>
            {returnsByOrder.map((returnData) => (
              <ReturnNotes returnData={returnData} key={returnData.id} />
            ))}
          </>
        )}

        {userDoc?.roles?.admin && (
          <div className="flex w-full flex-col gap-[2.4rem] rounded-2xl p-[2.4rem] lg:border-[1px]">
            <div className="text-[1.8rem flex w-full justify-between gap-[0.8rem] font-semibold">
              <span>Order SMS messages</span>
            </div>

            <SMSMessageList smsList={smsList ?? []} />
          </div>
        )}

        {userDoc?.roles?.admin && (
          <>
            <Button
              onClick={() => setAdminModalOpen(true)}
              text="Open Order Messages Modal"
            />

            <BaseModal
              isOpen={adminModalOpen}
              dismiss={() => setAdminModalOpen(false)}
              isFullScreen
            >
              <div className="h-[200vh] p-8">
                <MessageCenter chats={messages ?? []} variant="orderDetail" />
                <ContactCheckList order={order} />
              </div>
            </BaseModal>
          </>
        )}
      </div>
    </div>
  );
};

function OrderReviews({
  order,
  sellerId,
}: {
  order: OrderDocument;
  sellerId: string;
}) {
  const { data: review } = useQuery({
    queryKey: ['review', order.id, sellerId],
    queryFn: () =>
      getReviewByOrderIdAndSellerId({
        id: order.id,
        seller_id: sellerId,
      }),
    enabled: !!order.id,
  });

  const { data: sellerDoc } = useQuery({
    queryKey: ['publicUser', sellerId],
    queryFn: () => getPublicUserDoc({ uid: sellerId }),
    enabled: !!sellerId,
  });

  if (!review || !sellerDoc) return null;
  return (
    <div className="flex w-full flex-col gap-[1.6rem] rounded-2xl p-[2.4rem] lg:border-[1px]">
      <div className="text-[1.8rem] font-semibold">Reviews</div>
      <div className="flex flex-col gap-[1.2rem]">
        <ReviewCard
          review={review}
          productIds={order.sellers?.[sellerId]?.map((item) => item.id)}
        />
      </div>
    </div>
  );
}

const SMSMessageList = ({ smsList }: { smsList: SMSDocument[] }) => {
  return (
    <div className="flex flex-col gap-y-4">
      {!smsList?.length ? (
        <div className="flex h-[10rem] items-center justify-center">
          <span className="text-[1.6rem] font-medium text-zinc-600">
            No SMS messages found for this order
          </span>
        </div>
      ) : (
        <>
          <div role="list" aria-label="SMS messages for this order">
            {smsList.map((sms) => (
              <SMSMessage
                key={sms.id}
                id={sms.id}
                status={sms.status}
                msgType={sms.msg_type}
                to={sms.to}
                body={sms.body}
                created={sms.created}
                replies={sms.replies}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const SMSMessage = ({
  id,
  status,
  msgType,
  to,
  body,
  created,
  replies,
}: {
  id: string;
  status: string;
  msgType: string;
  to: string;
  body: string;
  created: number;
  replies: Array<{
    content: string;
    created: number;
    mime_type: string;
  }>;
}) => {
  const [showReplies, setShowReplies] = React.useState(true);

  return (
    <div
      role="listitem"
      className="flex w-full flex-col gap-y-8 rounded-[1rem] border border-zinc-200 bg-zinc-100 p-4"
      key={id}
      aria-label={`SMS message with status ${status} sent to ${to}`}
    >
      <div className="flex items-center gap-x-4">
        <div className="rounded-full bg-brand-secondary px-4 py-1 text-[1.5rem] text-white">
          {msgType}
        </div>

        <span className="text-[1.6rem] font-medium text-zinc-600">
          Message sent to {to}
        </span>
      </div>

      <p className="text-[1.6rem] font-medium text-black">{body}</p>

      <span className="text-zinc-600">{formatTimestamp(created)}</span>

      {!!replies.length && (
        <button
          type="button"
          className="flex items-center gap-x-2 text-[1.6rem] font-medium text-brand-secondary"
          onClick={() => setShowReplies(!showReplies)}
        >
          {showReplies ? (
            <ChevronUpIcon className="h-10 w-10 text-brand-secondary" />
          ) : (
            <ChevronDownIcon className="h-10 w-10 text-brand-secondary" />
          )}

          <span>{showReplies ? 'Hide' : 'Show'} replies</span>
        </button>
      )}

      {showReplies && (
        <div className="flex flex-col gap-y-10 px-8">
          {replies.map((reply) => (
            <div
              key={reply.created}
              className="flex w-fit flex-col gap-y-4 rounded-[1rem] border border-zinc-200 bg-zinc-200 p-6"
            >
              <span>{reply.content}</span>
              <span className="text-zinc-600">
                {formatTimestamp(reply.created)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrderDetailsPage;
